import { Box } from '@material-ui/core';
import FavoriteIcon from "@material-ui/icons/Favorite";
import ReplyIcon from '@material-ui/icons/Reply';
import { Paragraph, Position, Table, TableRowOwnProps, Tooltip } from 'evergreen-ui';
import React from 'react';

import {isAvailable, isPreferred, ReferralListColumn} from 'referral';

import type {ReferralWithMyOrgOffers} from '../../providerapp/hook/useReferralQueryForProvider$';
import {InquiryRequestIcon} from './InquiryRequestIcon';
import {OfferConversationPopup} from 'offer/component/conversation';
import { FeatureFlagSwitch, ProviderPortalFeatureFlag } from 'featureflag';


type ReferralListRowProps = {
    columns: Readonly<ReferralListColumn[]>;
    referral: Partial<ReferralWithMyOrgOffers>;
    selected: boolean;
    chosen: boolean;
} & TableRowOwnProps;

type SelectionStatusProp = {
    isSubmitted?: boolean;
}

function SubmissionStatus({ isSubmitted = false }: SelectionStatusProp) {
    const statusColor = (isSubmitted && '#44A895') || '#D8E3DC';
    const tooltip = (isSubmitted && 'Submitted') || 'Open';
    return (
        <Tooltip content={ tooltip }
                 position={ Position.RIGHT }
                 statelessProps={ { className: 'portal-ev-tooltip' } }
        >
            <ReplyIcon htmlColor={ statusColor } className="portal-provider-referral-submission-status-icon"/>
        </Tooltip>
    );
}

function PreferredStatus() {
    return (
        <div className="portal-provider-referral-favorite-icon">
            <FavoriteIcon />
        </div>
    );
}

type InquiryRequestBadgeProps = {
    requests: ReferralWithMyOrgOffers['incompleteReferralInquiryRequests'];
    hasPreferred: boolean;
}

function InquiryRequestBadge({ requests, hasPreferred }: InquiryRequestBadgeProps) {
    if (!requests?.length) return null;
    return (
        <div style={{ position: 'absolute', marginTop: -23, marginLeft: hasPreferred ? 62 : 34 }}>
            <InquiryRequestIcon requests={requests}/>
        </div>
    );
}

export const ReferralListRow = ({
                                    columns,
                                    referral,
                                    selected,
                                    chosen,
                                    ...tableRowProps
                                }: ReferralListRowProps) => {
    const isSubmitted = (referral.myOrgSubmittedOffers?.length ?? 0) > 0;
    const communicationOffer = referral.myOrgSubmittedOffers?.[0] ?? referral.myOrgAcceptedOffers?.[0];

    // note
    // selected is when the row is clicked, highlighted and its detail modal is open
    // chosen is when user wants to quickly view details of a specific referral and this row displays that referral
    return (
        <>
            <Table.Row isSelectable
                       isSelected={ selected }
                       minHeight={ 48 } height='auto'
                       backgroundColor="white"
                       elevation={ 2 }
                       className="rounded-lg my-3 py-4 portal-ev-tr"
                       position='relative'
                       { ...tableRowProps }>
                { isPreferred(referral) &&
                  <div style={{ position: 'absolute', marginTop: -22, marginLeft: 34 }}>
                    <PreferredStatus />
                  </div>
                }
                <InquiryRequestBadge requests={ referral.incompleteReferralInquiryRequests }
                                     hasPreferred={ isPreferred(referral) }/>

                { isAvailable(referral) &&
                <Box alignSelf='center'>
                  <SubmissionStatus isSubmitted={ isSubmitted }/>
                </Box>
                }
                {columns.map(def => def.name === 'communications' ? (
                    <Table.Cell
                        key={def.name}
                        flexBasis={def.basis ?? '1rem'}
                        flexShrink={def.size}
                        flexGrow={def.size}
                        paddingX={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {communicationOffer && (
                            <FeatureFlagSwitch flag={ProviderPortalFeatureFlag.BHPNPP_3077_IS_TWO_WAY_MESSAGING_DISABLED} negate>
                                <OfferConversationPopup
                                    offerGuid={communicationOffer.guid}
                                    conversationSummary={communicationOffer.conversationSummary}
                                    readOnly={!isSubmitted}
                                />
                            </FeatureFlagSwitch>
                        )}
                    </Table.Cell>
                ) : (
                    <Table.TextCell
                        key={def.name}
                        flexBasis={def.basis ?? '1rem'}
                        flexShrink={def.size}
                        flexGrow={def.size}
                    >
                        {def.render ? <def.render referral={referral}/> :
                            <Paragraph className="portal-ev-tr-td"
                                       size={500}
                                       overflow="hidden"
                                       whiteSpace="nowrap"
                                       textOverflow="ellipsis">
                                {referral[def.name]}</Paragraph>}
                    </Table.TextCell>
                ))}
            </Table.Row>
            { chosen && (
                <hr className="my-5"/>
            ) }
        </>
    );
};
